import React, { Component } from 'react';
import PropTypes from 'prop-types';
import DocumentTitle from '../components/DocumentTitle';
import Header from '../components/auth/Header';
import { JSONEditor } from '../components/core/JSONEditor'
import { useCertificateStore } from '../lib/stores/certificate'
import { useImageStore } from '../lib/stores/image'
import { useScheduledReportStore } from '../lib/stores/scheduledReport'
import { toast } from 'react-toastify'
import * as Sentry from '@sentry/react';
import __ from '../helpers/translation';
import localforage from 'localforage';

class Debug extends Component {
  
  constructor(props) {
    super(props);

    this.state = {
      debugInfo: {
        indexedDBUsage: null,
        indexedDBQuota: null,
        errors: [],
        stores: {
          certificate: null,
          image: null,
          scheduledReport: null,
        }
      },
    };

    this.resetClientData = this.resetClientData.bind(this);
  }

  async componentDidMount() {

    let debugInfo = { ...this.state.debugInfo };

    try {
      if(navigator.storage && navigator.storage.estimate) {
        const { usage, quota } = await navigator.storage.estimate();
        debugInfo.indexedDBUsage = usage;
        debugInfo.indexedDBQuota = quota;
      } 
      else {
        debugInfo.indexedDBUsage = 'Not supported by this browser';
        debugInfo.indexedDBQuota = 'Not supported by this browser';
      }

      debugInfo.stores.certificate = useCertificateStore.getState();
      debugInfo.stores.image = useImageStore.getState();
      debugInfo.stores.scheduledReport = useScheduledReportStore.getState();
    } 
    catch (err) {
      console.error(err);
      debugInfo.errors.push({
        message: 'Er is een fout opgetreden bij het ophalen van de storage info.',
        info: err.message
      });
    }

    this.setState({ debugInfo });
  }

  async resetClientData() {

    let errorOccurred = false;
    let debugInfo = { ...this.state.debugInfo };

    // 1. Reset localStorage en sessionStorage
    try {
      localStorage.clear();
      sessionStorage.clear();
      toast.success('Local storage en session storage is gereset.');
    } 
    catch(err) {
      console.error(err);
      debugInfo.errors.push({
        message: 'Er is een fout opgetreden bij het resetten van de local storage.',
        info: err.message
      })
      toast.error('Er is een fout opgetreden bij het resetten van de local storage.');
      errorOccurred = true;
    }

    // 2. Reset IndexedDB
    try {
      if(!errorOccurred && indexedDB.databases) {
        const dbs = await indexedDB.databases();
        for(const db of dbs) {
          if(db.name) indexedDB.deleteDatabase(db.name);
        }
        toast.success(`${dbs.length} IndexedDB database(s) is/zijn gereset.`);
      }
    } 
    catch(err) {
      console.error(err);
      debugInfo.errors.push({
        message: 'Er is een fout opgetreden bij het resetten van de IndexedDB.',
        info: err.message,
      });
      toast.error('Er is een fout opgetreden bij het resetten van de IndexedDB.');
      errorOccurred = true;
    }

    // 3. Reset Cache
    try {
      if(!errorOccurred && 'caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
        toast.success(`${keys.length} Cache(s) is/zijn gereset.`);
      }
    } 
    catch(err) {
      console.error(err);
      debugInfo.errors.push({
        message: 'Er is een fout opgetreden bij het resetten van de Cache.',
        info: err.message
      })
      toast.error('Er is een fout opgetreden bij het resetten van de Cache.');
      errorOccurred = true;
    }

    // 4. Reset Service Workers
    try {
      if(!errorOccurred && 'serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let reg of registrations) {
          await reg.unregister();
        }
        toast.success(`${registrations.length} Service Worker(s) is/zijn gereset.`);
      }
    } 
    catch(err) {
      console.error(err);
      debugInfo.errors.push({
        message: 'Er is een fout opgetreden bij het resetten van Service Workers.',
        info: err.message
      });
      toast.error('Er is een fout opgetreden bij het resetten van de Service Workers.');
      errorOccurred = true;
    }

    await localforage.setDriver(localforage.INDEXEDDB);
    const user = await localforage.getItem('bs_user');

    Sentry.captureEvent({
      level: 'info',
      message: 'User did a full client reset.',
      user: user ? { 
        id: user.id,
        email: user.email,
        username: user.name,
      } : null,
      extra: {
        debugInfo: JSON.stringify(debugInfo),
        user: user ?? null,
        errorInResetProcess: errorOccurred,
      }
    });

    this.setState({ debugInfo });

    // 5. Reload the page if no errors occurred
    if(!errorOccurred) {
      setTimeout(() => {
        window.location.reload();
      }, 3500);
    } 
    else {
      // Als er een fout is, NIET reloaden, maar error tonen in textarea.
      // Dit is al in debugInfo.errors gezet, deze wordt in de textarea getoond.
      toast.error('Er is een fout opgetreden, zie textarea voor details.');
    }
  }

  render () {
    const { debugInfo } = this.state;

    return (
      <DocumentTitle title={`Debug info - ${window.App.name}`}>
        
        <Header title="Debug info" />

        <div className="tw-flex tw-justify-center">
          <div className="tw-max-w-xl tw-w-full">
            <h4 className="!tw-font-semibold !tw-text-xl tw-mt-4">
              Algemene debugging informatie
            </h4>
            <p className="tw-mt-2 tw-text-md tw-text-neutral-500 tw-mb-0">
              Deze pagina is enkel voor het opzoeken van fouten binnen het programma. Dit is een tijdelijke pagina, die enkel op aanvraag van Batteryspray gebruikt mag worden. Gebruik van deze pagina zonder toestemming is op eigen risico.
            </p>

            <button onClick={this.resetClientData} className='tw-mt-4 tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-border tw-bg-white tw-border-red-300 tw-text-red-500 tw-font-bold hover:tw-bg-neutral-100 tw-transition-all active:tw-scale-95 tw-block'>
              Reset client
            </button>

            <p className="tw-mt-4 tw-text-md tw-text-neutral-500">
              Met bovenstaande button gaat alle niet-opgeslagen data verloren.
              Met deze reset-actie worden alle lokale opslag, sessie opslag, IndexedDB, Cache en Service Workers gereset. Ook worden de nieuwste 
              versies van de assets opgehaald.
            </p>

            <h4 className="!tw-font-semibold !tw-text-xl tw-mt-4">
              Extra informatie
            </h4>
            <JSONEditor 
              className='tw-mt-2 tw-w-full tw-h-full tw-max-h-[20rem] tw-overflow-hidden tw-rounded-lg tw-mb-4'
              value={JSON.stringify(debugInfo, null, 2)}
            />

          </div>
        </div>

      </DocumentTitle>
    )
  }
}

export default Debug;
